import {
  Box,
  Chip,
  ListItemButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { Hangar, Location, PlacementWithLocation, Ramp, Stack as StackType, Tenant } from "../../types";
import { AircraftActionMenu } from "./AircraftActionMenu";
import { TripIndicator } from "./TripIndicator";
import {
  TripLocationNavigationDialog,
  LocationInfo,
  TripInfo
} from "../../screens/Schedule/TripLocationNavigationDialog/TripLocationNavigationDialog";

interface AircraftWithTrips {
  tailNumber: string;
  tenantId?: string;
  aircraftModel?: string;
  aircraftMake?: string;
  trips: Array<{
    tripId: string;
    start: Date;
    end: Date;
    locationId: string;
    locationType: 'hangar' | 'ramp';
    locationName?: string;
    status?: 'scheduled' | 'active' | 'completed' | 'cancelled';
  }>;
  isCurrentlyHere: boolean;
  tenant?: Tenant;
}

interface Props {
  aircraft: AircraftWithTrips;
  tenant?: Tenant;
  stack: StackType;
  setStack: (stack: StackType) => void;
  setTenant: (tenant: Tenant) => void;
  onSelectAircraft: (tenant: Tenant) => void;
  onDeleteAircraft: (tenant: Tenant) => void;
  showBottomBorder: boolean;
  rangeStart: Date;
  rangeEnd: Date;
  currentTime: Date;
  location: Location<Hangar | Ramp>;
  onAircraftClick?: (aircraft: AircraftWithTrips) => void;
  onRefresh?: () => void;
  onTimeChange?: (time: Date) => void;
  onNavigateToLocation?: (locationId: string, locationType: string, time: Date) => void;
  // Spot detection props
  currentPlacement?: PlacementWithLocation;
}

export const TimelineAircraftListItem: React.FC<Props> = ({
  aircraft,
  tenant,
  stack,
  setStack,
  setTenant,
  onSelectAircraft,
  onDeleteAircraft,
  showBottomBorder,
  rangeStart,
  rangeEnd,
  currentTime,
  location,
  onAircraftClick,
  onRefresh,
  onTimeChange,
  onNavigateToLocation,
  currentPlacement,
}) => {
  // Mark unused props to suppress TS errors - these are kept for interface compatibility
  void stack;
  void setStack;
  void setTenant;
  void onDeleteAircraft;
  void onAircraftClick;
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(false);

  // Dialog state for navigation
  const [navigationDialog, setNavigationDialog] = React.useState<{
    open: boolean;
    targetLocation: LocationInfo;
    tripInfo: TripInfo;
  }>({
    open: false,
    targetLocation: { id: '', name: '', type: 'unassigned' },
    tripInfo: { tailNumber: '', startTime: new Date(), endTime: new Date() },
  });

  // Check if any trip is cancelled
  const hasCancelledTrip = aircraft.trips.some(trip => trip.status === 'cancelled');

  // Determine display color based on state
  const displayColor = hasCancelledTrip
    ? '#F44336' // Red for cancelled
    : aircraft.isCurrentlyHere
    ? theme.palette.primary.main
    : theme.palette.text.secondary;

  // Get current location info with enhanced status
  const locationStatus = React.useMemo(() => {
    const now = currentTime.getTime();
    const currentTrip = aircraft.trips.find(trip => {
      const start = trip.start.getTime();
      const end = trip.end.getTime();
      return start <= now && end >= now;
    });

    if (currentTrip) {
      // Check if cancelled first
      if (currentTrip.status === 'cancelled') {
        return {
          text: 'CANCELLED',
          color: 'error' as const,
          variant: 'filled' as const
        };
      }

      if (currentTrip.locationId === location.id) {
        return {
          text: 'HERE',
          color: 'success' as const,
          variant: 'filled' as const
        };
      } else if (currentTrip.locationName) {
        // Handle "Unassigned" location name from database
        if (currentTrip.locationName === "Unassigned" || !currentTrip.locationId) {
          return {
            text: 'UNASSIGNED',
            color: 'warning' as const,
            variant: 'outlined' as const
          };
        }
        return {
          text: `AT ${currentTrip.locationName.toUpperCase()}`,
          color: 'default' as const,
          variant: 'outlined' as const
        };
      } else if (!currentTrip.locationId) {
        // No location ID means unassigned
        return {
          text: 'UNASSIGNED',
          color: 'warning' as const,
          variant: 'outlined' as const
        };
      }
    }

    // Find next arrival
    const nextTrip = aircraft.trips
      .filter(trip => trip.start.getTime() > now)
      .sort((a, b) => a.start.getTime() - b.start.getTime())[0];

    if (nextTrip) {
      const hoursUntil = Math.floor((nextTrip.start.getTime() - now) / (1000 * 60 * 60));
      if (hoursUntil < 24) {
        return {
          text: `ARRIVES IN ${hoursUntil}H`,
          color: 'info' as const,
          variant: 'outlined' as const
        };
      } else {
        const daysUntil = Math.floor(hoursUntil / 24);
        return {
          text: `ARRIVES IN ${daysUntil}D`,
          color: 'default' as const,
          variant: 'outlined' as const
        };
      }
    }

    return null;
  }, [aircraft.trips, currentTime, location.id]);

  // Format aircraft info
  const aircraftInfo = React.useMemo(() => {
    if (aircraft.aircraftModel) {
      return aircraft.aircraftModel;
    }
    if (aircraft.aircraftMake) {
      return aircraft.aircraftMake;
    }
    return "Unknown Aircraft";
  }, [aircraft.aircraftModel, aircraft.aircraftMake]);


  // Get smart trip summary
  const tripSummary = React.useMemo(() => {
    if (aircraft.trips.length === 0) return null;

    const sortedTrips = [...aircraft.trips].sort((a, b) => a.start.getTime() - b.start.getTime());

    const formatDateCompact = (date: Date) => {
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    };

    const formatDateRange = (start: Date, end: Date) => {
      const startStr = formatDateCompact(start);
      const endStr = formatDateCompact(end);
      if (startStr === endStr) return startStr;

      const startMonth = start.toLocaleDateString(undefined, { month: 'short' });
      const endMonth = end.toLocaleDateString(undefined, { month: 'short' });
      if (startMonth === endMonth) {
        return `${startMonth} ${start.getDate()}-${end.getDate()}`;
      }
      return `${startStr}-${endStr}`;
    };

    // Get unique locations
    const uniqueLocations = new Set(
      sortedTrips.map(t => t.locationName || `${t.locationType} ${t.locationId}`)
    );

    // Get overall date range
    const firstTrip = sortedTrips[0];
    const lastTrip = sortedTrips[sortedTrips.length - 1];
    const overallDateRange = formatDateRange(firstTrip.start, lastTrip.end);

    // Determine location text
    let locationText = '';
    if (uniqueLocations.size === 1) {
      locationText = Array.from(uniqueLocations)[0];
    } else {
      locationText = `${uniqueLocations.size} locations`;
    }

    // For 1-2 trips, return individual trips for inline display
    if (sortedTrips.length <= 2) {
      return {
        type: 'inline' as const,
        trips: sortedTrips.map(trip => ({
          trip,
          text: `${formatDateRange(trip.start, trip.end)} @ ${trip.locationName || `${trip.locationType} ${trip.locationId}`}`,
          isHere: trip.locationId === location.id,
          isCancelled: trip.status === 'cancelled',
        })),
      };
    }

    // For 3+ trips, return summary
    return {
      type: 'summary' as const,
      summaryText: `${sortedTrips.length} trips: ${overallDateRange} @ ${locationText}`,
      trips: sortedTrips.map(trip => ({
        trip,
        text: `${formatDateRange(trip.start, trip.end)} @ ${trip.locationName || `${trip.locationType} ${trip.locationId}`}`,
        isHere: trip.locationId === location.id,
        isCancelled: trip.status === 'cancelled',
      })),
      firstTrip: sortedTrips[0],
      isAnyHere: sortedTrips.some(t => t.locationId === location.id),
    };
  }, [aircraft.trips, location.id]);

  // Get spot name from placement data (calculated at data source)
  const currentSpotName = currentPlacement?.spot?.text;

  const isSelectable = !!tenant;
  const isSelected = tenant?.selected || false;

  // Handle trip click with location check
  const handleTripClick = React.useCallback(
    (trip: any, e: React.MouseEvent) => {
      e.stopPropagation();

      // Check if trip is at a different location than current view
      const isDifferentLocation = trip.locationId !== location.id;
      const isUnassigned = !trip.locationId || trip.locationType === 'unassigned';

      if (isDifferentLocation || isUnassigned) {
        // Show navigation dialog
        setNavigationDialog({
          open: true,
          targetLocation: {
            id: trip.locationId || '',
            name: trip.locationName || 'Unassigned',
            type: isUnassigned ? 'unassigned' : trip.locationType,
          },
          tripInfo: {
            tailNumber: aircraft.tailNumber,
            startTime: trip.start,
            endTime: trip.end,
          },
        });
      } else {
        // Same location, just jump to time
        onTimeChange?.(trip.start);
      }
    },
    [location.id, aircraft.tailNumber, onTimeChange]
  );

  // Add state for menu anchor to programmatically open menu
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    // If aircraft is currently here and has a tenant, toggle selection
    if (isSelectable && tenant) {
      onSelectAircraft(tenant);
    } else {
      // For aircraft not currently here, trigger the action menu click
      // This makes the entire row interactive
      menuButtonRef.current?.click();
    }
  };

  return (
    <Box
      sx={{
        borderBottom: !showBottomBorder ? 1 : 0,
        borderColor: "divider",
        bgcolor: isSelected
          ? `${theme.palette.success.main}15`
          : aircraft.isCurrentlyHere
          ? `${theme.palette.primary.main}05`
          : "transparent",
        "&:hover": {
          backgroundColor: isSelected
            ? `${theme.palette.success.main}20`
            : `${theme.palette.action.hover}`,
        },
        transition: "background-color 0.2s",
        display: "flex",
        alignItems: "stretch",
      }}
    >
      {/* Main clickable area */}
      <ListItemButton
        onClick={handleClick}
        sx={{
          flex: 1,
          p: 0,
          "&:hover": {
            backgroundColor: "transparent", // Handled by parent Box
          },
        }}
      >
        <Stack
          direction="column"
          spacing={0.25}
          sx={{
            flex: 1,
            p: 1,
            minWidth: 0,
          }}
        >
        {/* Aircraft info row with location status */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: aircraft.isCurrentlyHere ? 600 : 500,
                color: displayColor,
              }}
            >
              {aircraft.tailNumber}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
              }}
              noWrap
            >
              {aircraftInfo}
            </Typography>
          </Stack>
          {/* Location status chip simplified */}
          {locationStatus && (
            <Chip
              label={locationStatus.text === 'HERE' ? 'HERE NOW' : locationStatus.text}
              color={locationStatus.color}
              variant={locationStatus.variant}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: 0.75,
                },
              }}
            />
          )}
          {/* Spot detection chip - only show for HERE NOW aircraft with detected spot */}
          {locationStatus?.text === 'HERE' && currentSpotName && (
            <Chip
              label={currentSpotName}
              variant="outlined"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 500,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '& .MuiChip-label': {
                  px: 0.75,
                },
              }}
            />
          )}
        </Stack>

        {/* Trip indicator */}
        <TripIndicator
          trips={aircraft.trips}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          currentTime={currentTime}
          currentLocationId={location.id}
          onTimeChange={(time) => {
            // For now, TripIndicator just jumps to time
            // Could be enhanced to also check location
            onTimeChange?.(time);
          }}
        />

        {/* Smart trip display */}
        {tripSummary && (
          <>
            {tripSummary.type === 'inline' ? (
              // For 1-2 trips, show inline
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                {tripSummary.trips.map((item, idx) => (
                  <React.Fragment key={`inline-${aircraft.tailNumber}-${idx}`}>
                    <Typography
                      variant="caption"
                      onClick={(e) => handleTripClick(item.trip, e)}
                      sx={{
                        fontSize: '0.75rem',
                        color: item.isCancelled
                          ? 'error.main'
                          : item.isHere
                          ? 'text.primary'
                          : 'text.secondary',
                        textDecoration: item.isCancelled ? 'line-through' : 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {item.text}
                    </Typography>
                    {idx < tripSummary.trips.length - 1 && (
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        ,
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Stack>
            ) : (
              // For 3+ trips, show summary with expansion
              <Stack spacing={0.25}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.75rem',
                      color: tripSummary.isAnyHere ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {tripSummary.summaryText}
                  </Typography>
                  <Typography
                    variant="caption"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(!expanded);
                    }}
                    sx={{
                      fontSize: '0.7rem',
                      color: 'primary.main',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {expanded ? 'show less' : 'show all'}
                  </Typography>
                </Stack>
                {expanded && (
                  <Stack spacing={0} sx={{ pl: 1 }}>
                    {tripSummary.trips.map((item, idx) => (
                      <Typography
                        key={`expanded-${aircraft.tailNumber}-${idx}`}
                        variant="caption"
                        onClick={(e) => handleTripClick(item.trip, e)}
                        sx={{
                          fontSize: '0.7rem',
                          color: item.isCancelled
                            ? 'error.main'
                            : item.isHere
                            ? 'success.main'
                            : 'text.secondary',
                          textDecoration: item.isCancelled ? 'line-through' : 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        • {item.text}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Stack>
            )}
          </>
        )}
      </Stack>
      </ListItemButton>

      {/* Action menu - always show for all aircraft */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          pr: 1,
        }}
      >
        <AircraftActionMenu
          aircraft={aircraft}
          currentLocation={location}
          currentTime={currentTime}
          onRefresh={onRefresh || (() => {})}
          onTimeChange={onTimeChange || (() => {})}
        />
      </Box>

      {/* Navigation Dialog */}
      <TripLocationNavigationDialog
        open={navigationDialog.open}
        onClose={() => setNavigationDialog({ ...navigationDialog, open: false })}
        currentLocation={{
          id: location.id,
          name: location.name || 'Unknown',
          type: location.type as 'hangar' | 'ramp',
        }}
        targetLocation={navigationDialog.targetLocation}
        tripInfo={navigationDialog.tripInfo}
        onNavigateToLocation={onNavigateToLocation || (() => {})}
        onStayCurrentLocation={(time) => {
          onTimeChange?.(time);
          setNavigationDialog({ ...navigationDialog, open: false });
        }}
      />
    </Box>
  );
};